/**
 * True when the PWA is being served from the suite subdomain
 * (`opbuttons.overpoweredapps.net` or a Pages preview), as opposed to the LAN
 * dev-server (`localhost:5183`) or a bare LAN IP (DEC-166). Shared by
 * `SignInGate.tsx` (OP ID's master cookie can only ever exist on this domain)
 * and `opbridge/discovery.ts` (a broadcast LAN scan is mixed-content-blocked
 * wholesale from here — see `suiteHostedCandidates()`).
 */
export function isOnSuiteDomain(): boolean {
  try {
    return /(^|\.)overpoweredapps\.net$/i.test(window.location.hostname);
  } catch {
    return false;
  }
}

/**
 * True when running as an INSTALLED web app (iOS "Add to Home Screen",
 * or an installed PWA on desktop/Android) rather than in a browser tab.
 *
 * Load-bearing because iOS treats the two as different applications:
 * an installed web app gets its OWN storage container, and the camera
 * hands a scanned QR link to **Safari**, never to the installed app. So
 * the QR pairing flow pairs Safari and leaves the installed app empty —
 * they must be paired separately, and the installed one can only be
 * paired from inside itself (paste/type the address). Found on a real
 * iPad 2026-08-30.
 *
 * `display-mode: standalone` is the standard signal; `navigator.standalone`
 * is the older iOS-only one and is still what older iPadOS reports, so
 * both are checked.
 */
export function isInstalledApp(): boolean {
  try {
    const iosLegacy = (window.navigator as unknown as { standalone?: boolean }).standalone === true;
    const standard = window.matchMedia?.('(display-mode: standalone)').matches === true;
    return iosLegacy || standard;
  } catch {
    return false;
  }
}
